(() => {
  if (customElements.get("cinematic-slider")) return;

  class CinematicSlider extends SliderComponent {
    constructor() {
      super();
      this.items = [];
      this.videos = [];
      this.videoMap = new Map();
      this.muteButtonMap = new Map();
      this.activeIndex = 0;
      this.enableVideoAutoplay = true;
      this.isSnapping = false;
      this.snapTimeout = null;
      this.rafId = null;
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.currentOpener = null;
      this._popoverFocusTrap = null;
      this.cartDrawer = null;

      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.cartErrorEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) ||
        "cart-error";
    }

    connectedCallback() {
      if (this.isConnected && this.hasAttribute("data-cinematic-ready")) return;
      this.setAttribute("data-cinematic-ready", "true");

      // This component is built on physical left-to-right scroll math (scrollLeft/offsetLeft).
      // Force LTR so it behaves consistently on RTL pages.
      if (!this.hasAttribute("dir")) {
        this.setAttribute("dir", "ltr");
      }

      this.track = this.querySelector("[data-slider-track]");
      this.items = Array.from(this.querySelectorAll("[data-cinematic-item]"));
      this.videos = Array.from(this.querySelectorAll("[data-cinematic-video]"));
      this.cartDrawer = document.getElementById("Cart-Drawer");
      this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
      this.videoMap = new Map();
      this.videos.forEach((video) => {
        const index = Number(video.dataset.cinematicVideoIndex);
        if (!Number.isNaN(index)) {
          this.videoMap.set(index, video);
        }
      });
      if (!this.videoMap.size) {
        this.items.forEach((item, index) => {
          const video = item.querySelector("video");
          if (video) this.videoMap.set(index, video);
        });
        this.videos = Array.from(this.videoMap.values());
      }

      if (!this.track || !this.items.length) return;

      this.cacheItems();
      this.cacheMuteButtons();
      this.bindMuteControls();
      this.updateToggleStates(this.activeIndex);

      super.connectedCallback();
      this.addEventListener("click", this.onClick);
      document.addEventListener("keydown", this.onKeyDown, { capture: true });

      requestAnimationFrame(() => {
        this.updateActive();
        this.scrollToIndex(this.activeIndex, false);
        this.setActive(this.activeIndex);
      });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      document.removeEventListener("keydown", this.onKeyDown, {
        capture: true,
      });
      clearTimeout(this.snapTimeout);
      cancelAnimationFrame(this.rafId);
      super.disconnectedCallback();
      this.removeAttribute("data-cinematic-ready");
    }

    // This component forces dir="ltr" on itself so scroll math stays physical.
    // Override the parent's RTL detection to prevent RTL scroll normalization.
    getIsRtl() {
      return false;
    }

    cacheItems() {
      this.itemData = this.items.map((item) => ({
        el: item,
        center: item.offsetLeft + item.offsetWidth / 2,
      }));
    }

    getVideoAutoplayForVideo(video) {
      if (!video) return null;
      return video.closest("video-autoplay");
    }

    cacheMuteButtons() {
      this.muteButtonMap = new Map();
      this.items.forEach((item) => {
        const index = Number(item.dataset.index);
        if (Number.isNaN(index)) return;
        const muteButton = item.querySelector("[data-mute-button]");
        if (muteButton) {
          this.muteButtonMap.set(index, muteButton);
        }
      });
    }

    bindMuteControls() {
      if (!this.muteButtonMap.size) return;
      this.videos.forEach((video) => {
        const index = Number(video.dataset.cinematicVideoIndex);
        if (Number.isNaN(index)) return;
        video.addEventListener("volumechange", () => this.updateMuteUi(index));
        video.addEventListener("play", () => this.syncMuteButtonStates());
        video.addEventListener("pause", () => this.syncMuteButtonStates());
        video.addEventListener("ended", () => this.syncMuteButtonStates());
        this.updateMuteUi(index);
      });
      this.syncMuteButtonStates();
    }

    updateMuteUi(index) {
      const video = this.videoMap.get(index);
      const button = this.muteButtonMap.get(index);
      if (!video || !button) return;
      const isMuted = video.muted;
      const mutedLabel = button.dataset.labelMuted || "Muted";
      const unmutedLabel = button.dataset.labelUnmuted || "Unmuted";
      button.setAttribute("aria-label", isMuted ? mutedLabel : unmutedLabel);
      button.setAttribute("aria-pressed", isMuted ? "true" : "false");
      const mutedIcon = button.querySelector("[data-icon-muted]");
      const unmutedIcon = button.querySelector("[data-icon-unmuted]");
      if (mutedIcon) {
        mutedIcon.classList.toggle("hidden", !isMuted);
      }
      if (unmutedIcon) {
        unmutedIcon.classList.toggle("hidden", isMuted);
      }
    }

    syncMuteButtonStates(activeIndex = this.activeIndex) {
      if (!this.muteButtonMap.size) return;
      this.muteButtonMap.forEach((button, index) => {
        const video = this.videoMap.get(index);
        if (!video) {
          button.classList.add("hidden");
          return;
        }
        const isPlaying = !video.paused && !video.ended;
        const shouldShow = this.enableVideoAutoplay
          ? index === activeIndex
          : isPlaying;
        button.classList.toggle("hidden", !shouldShow);
        this.updateMuteUi(index);
      });
    }

    resetMuteStates() {
      if (!this.videos.length) return;
      this.videos.forEach((video) => {
        const autoplay = this.getVideoAutoplayForVideo(video);
        autoplay?.toggleMute(true);
      });
      if (!this.muteButtonMap.size) return;
      this.muteButtonMap.forEach((_, index) => this.updateMuteUi(index));
    }

    onScroll() {
      super.onScroll();
      this.requestActiveUpdate();
    }

    onSliderKeyDown(event) {
      if (!this.isEnabled) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      var tag = document.activeElement && document.activeElement.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (document.activeElement && document.activeElement.isContentEditable)) return;
      event.preventDefault();
      if (event.key === "ArrowLeft") {
        this.onPrevClick();
      } else {
        this.onNextClick();
      }
      // Focus the new active card
      const active = this.items[this.activeIndex];
      if (active) active.focus({ preventScroll: true });
    }

    onPrevClick() {
      if (!this.items.length) return;
      this.goTo(this.activeIndex - 1);
    }

    onNextClick() {
      if (!this.items.length) return;
      this.goTo(this.activeIndex + 1);
    }

    // Use own activeIndex instead of parent's scroll-position-based getActiveIndex,
    // which can misfire during smooth-scroll animations and disable buttons too early.
    updateButtons() {
      if (!this.prevButton || !this.nextButton) return;
      this.prevButton.disabled = this.activeIndex <= 0;
      this.nextButton.disabled = this.activeIndex >= this.items.length - 1;
    }

    onResize() {
      super.onResize();
      this.cacheItems();
      this.requestActiveUpdate();
    }

    requestActiveUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        if (!this.isSnapping) this.updateActive();
        this.rafId = null;
      });
    }

    updateActive() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const isAtEnd = this.track.scrollLeft >= maxScroll - 2;
      if (isAtEnd) {
        const lastIndex = this.items.length - 1;
        if (lastIndex !== this.activeIndex) {
          this.setActive(lastIndex);
        }
        return;
      }
      let closest = 0;
      let minDist = Infinity;

      this.itemData.forEach((item, index) => {
        const dist = Math.abs(item.center - center);
        if (dist < minDist) {
          minDist = dist;
          closest = index;
        }
      });

      if (closest !== this.activeIndex) {
        this.setActive(closest);
      }
    }

    setActive(index) {
      this.closeAllPopovers();

      const prev = this.items[this.activeIndex];
      prev?.setAttribute("data-active", "false");
      prev?.setAttribute("aria-current", "false");
      prev?.setAttribute("tabindex", "-1");

      const next = this.items[index];
      next?.setAttribute("data-active", "true");
      next?.setAttribute("aria-current", "true");
      next?.setAttribute("tabindex", "0");

      this.activeIndex = index;

      this.updateToggleStates(index);

      this.resetMuteStates();

      if (this.enableVideoAutoplay) {
        const activeVideo = this.videoMap.get(index);
        this.videos.forEach((video) => {
          const autoplay = this.getVideoAutoplayForVideo(video);
          if (video === activeVideo) {
            autoplay?.play();
          } else {
            autoplay?.pause();
            try {
              video.currentTime = 0;
            } catch (e) {}
          }
        });
      } else {
        this.items.forEach((item) => {
          const itemIndex = Number(item.dataset.index);
          if (Number.isNaN(itemIndex)) return;
          const video = this.videoMap.get(itemIndex);
          if (video) {
            const autoplay = this.getVideoAutoplayForVideo(video);
            autoplay?.pause();
            try {
              video.currentTime = 0;
            } catch (e) {}
          }
        });
      }

      this.syncMuteButtonStates(index);
      this.updateButtons();
    }

    updateToggleStates(index) {
      this.items.forEach((item, i) => {
        const isActive = i === index;
        const toggle = item.querySelector("[data-cinematic-toggle]");
        const play = item.querySelector("[data-play-button]");
        if (toggle) {
          toggle.classList.toggle("scale-110", isActive);
          if (isActive) {
            if (!toggle.hasAttribute("data-toggle-disabled")) {
              toggle.removeAttribute("disabled");
              toggle.setAttribute("aria-disabled", "false");
            }
          } else {
            toggle.setAttribute("disabled", "");
            toggle.setAttribute("aria-disabled", "true");
          }
        }

        if (play) {
          if (!this.enableVideoAutoplay) {
            if (isActive) {
              play.removeAttribute("hidden");
              play.classList.remove("hidden");
            } else {
              play.setAttribute("hidden", "");
              play.classList.add("hidden");
            }
          }
          if (isActive) {
            play.removeAttribute("disabled");
            play.setAttribute("aria-disabled", "false");
            play.classList.remove("pointer-events-none");
          } else {
            play.setAttribute("disabled", "");
            play.setAttribute("aria-disabled", "true");
            play.classList.add("pointer-events-none");
          }
        }
      });
    }

    goTo(index) {
      index = Math.max(0, Math.min(index, this.items.length - 1));
      this.isSnapping = true;
      this.scrollToIndex(index, true);
    }

    scrollToIndex(index, smooth = true) {
      const item = this.itemData[index];
      if (!item) return;

      const offset =
        item.el.offsetLeft - (this.track.clientWidth - item.el.offsetWidth) / 2;

      if (smooth) {
        this.track.style.scrollSnapType = "none";
      }

      if (index !== this.activeIndex) {
        this.setActive(index);
      }

      this.track.scrollTo({
        left: offset,
        behavior: smooth ? "smooth" : "auto",
      });

      if (smooth) {
        clearTimeout(this.snapTimeout);
        this.snapTimeout = setTimeout(() => {
          this.isSnapping = false;
          this.track.style.scrollSnapType = "";
          this.updateActive();
        }, 350);
      } else {
        this.isSnapping = false;
        this.track.style.scrollSnapType = "";
      }
    }

    snapToNearest() {
      if (!this.track || !this.items.length) return;
      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const centerOffset =
        (this.track.clientWidth - this.items[0].clientWidth) / 2;
      let nearestIndex = 0;
      let smallest = Infinity;

      this.items.forEach((item, index) => {
        const target = item.offsetLeft - centerOffset;
        const distance = Math.abs(target - scrollLeft);
        if (distance < smallest) {
          smallest = distance;
          nearestIndex = index;
        }
      });

      const target = this.items[nearestIndex]
        ? this.items[nearestIndex].offsetLeft - centerOffset
        : 0;
      const clamped = Math.max(0, Math.min(maxScroll, target));
      this.track.style.scrollBehavior = "smooth";
      this.track.scrollTo({ left: clamped, behavior: "smooth" });
    }

    onClick(event) {
      const toggle = event.target.closest("[data-cinematic-toggle]");
      if (toggle) {
        event.preventDefault();
        this.togglePopover(toggle);
        return;
      }
      const close = event.target.closest("[data-cinematic-close]");
      if (close) {
        event.preventDefault();
        const opener = this.getOpenerById(close.dataset.cinematicClose);
        this.closePopover(opener);
        return;
      }
      const add = event.target.closest("[data-cinematic-add]");
      if (add) {
        event.preventDefault();
        this.handleAddToCart(add);
      }
    }

    onKeyDown(event) {
      // Escape to close popover
      var isEscape =
        event.key === "Escape" ||
        event.key === "Esc" ||
        event.code === "Escape" ||
        event.keyCode === 27;
      if (isEscape) {
        if (!this.currentOpener) return;
        event.preventDefault();
        this.closePopover(this.currentOpener);
        return;
      }
    }

    getOpenerById(id) {
      if (!id) return null;
      return this.querySelector(`[data-cinematic-toggle="${id}"]`);
    }

    openPopover(opener) {
      if (!opener) return;
      const id = opener.dataset.cinematicToggle;
      if (!id) return;
      const popover = this.querySelector(`[data-cinematic-popover="${id}"]`);
      if (!popover) return;
      this.closeAllPopovers();
      popover.removeAttribute("hidden");
      opener.setAttribute("aria-expanded", "true");
      this.currentOpener = opener;

      // Focus trap for popover dialog
      this._popoverFocusTrap = webvista.createFocusTrap(popover, {
        onEscape: () => this.closePopover(opener),
      });
      popover.addEventListener("keydown", this._popoverFocusTrap);

      // Auto-focus first element
      webvista.focusFirstElement(popover);
    }

    closePopover(opener) {
      if (!opener) return;
      const id = opener.dataset.cinematicToggle;
      if (!id) return;
      const popover = this.querySelector(`[data-cinematic-popover="${id}"]`);
      if (!popover) return;

      // Remove focus trap
      if (this._popoverFocusTrap) {
        popover.removeEventListener("keydown", this._popoverFocusTrap);
        this._popoverFocusTrap = null;
      }

      popover.setAttribute("hidden", "");
      opener.setAttribute("aria-expanded", "false");
      opener.focus();
      if (this.currentOpener === opener) {
        this.currentOpener = null;
      }
    }

    togglePopover(opener) {
      if (!opener) return;
      const id = opener.dataset.cinematicToggle;
      if (!id) return;
      const popover = this.querySelector(`[data-cinematic-popover="${id}"]`);
      if (!popover) return;
      const isOpen = !popover.hasAttribute("hidden");
      if (isOpen) {
        this.closePopover(opener);
      } else {
        this.openPopover(opener);
      }
    }

    closeAllPopovers() {
      // Remove focus trap from any open popover
      if (this._popoverFocusTrap) {
        this.querySelectorAll("[data-cinematic-popover]").forEach((popover) => {
          popover.removeEventListener("keydown", this._popoverFocusTrap);
        });
        this._popoverFocusTrap = null;
      }

      this.querySelectorAll("[data-cinematic-popover]").forEach((popover) => {
        popover.setAttribute("hidden", "");
      });
      this.querySelectorAll("[data-cinematic-toggle]").forEach((toggle) => {
        toggle.setAttribute("aria-expanded", "false");
      });
      this.currentOpener = null;
    }

    async openCartDrawer(opener) {
      const header = document.querySelector("header-component");
      const cartType = header?.dataset.cartType || "drawer";
      const cartOpenOnAdd = header?.dataset.cartOpenOnAdd !== "false";
      if (!cartOpenOnAdd) {
        if (window.location.pathname === (window.routes?.cart_url || "/cart")) {
          window.location.reload();
        }
        return;
      }
      if (cartType === "page") {
        const cartUrl = window.routes?.cart_url || "/cart";
        if (window.location.pathname === cartUrl) {
          window.location.reload();
        } else {
          window.location.href = cartUrl;
        }
        return;
      }
      if (!this.cartDrawer) {
        this.cartDrawer = document.getElementById("Cart-Drawer");
      }
      if (!this.cartDrawer) return;
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
        return;
      }
      await customElements.whenDefined("cart-drawer");
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
      }
    }

    async handleAddToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      const idle = button.querySelector("[data-cinematic-add-idle]");
      const adding = button.querySelector("[data-cinematic-add-adding]");
      button.classList.add("loading");
      button.setAttribute("aria-busy", "true");
      if (idle) {
        idle.classList.add("opacity-0", "-translate-y-4");
      }
      if (adding) {
        adding.classList.remove("opacity-0", "translate-y-4");
      }
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");

      try {
        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", 1);

        const sections = this.getSectionsToRender();
        if (sections.length) {
          formData.append(
            "sections",
            sections.map((section) => section.section).join(","),
          );
          formData.append("sections_url", window.location.pathname);
        }

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "X-Requested-With": "XMLHttpRequest" },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          document.dispatchEvent(
            new CustomEvent(this.cartErrorEvent, {
              detail: { error: errorData },
            }),
          );
          return;
        }

        const data = await response.json();
        if (data?.sections) {
          this.renderSections(data.sections);
        }
        document.dispatchEvent(
          new CustomEvent(this.cartUpdateEvent, { detail: { variantId } }),
        );
        // Find the toggle button to use as focus-return target
        const blockId = button.closest("[data-cinematic-popover]")?.dataset.cinematicPopover;
        const toggle = blockId
          ? this.querySelector(`[data-cinematic-toggle="${blockId}"]`)
          : null;
        this.closeAllPopovers();
        if (this.cartDrawer && this.cartDrawer.hasAttribute("open")) {
          // already open
        } else if (this.cartDrawer) {
          await this.openCartDrawer(toggle || button);
        } else {
          window.location.href = "/cart";
        }
      } catch (error) {
        document.dispatchEvent(
          new CustomEvent(this.cartErrorEvent, { detail: { error } }),
        );
      } finally {
        button.classList.remove("loading");
        button.setAttribute("aria-busy", "false");
        if (idle) {
          idle.classList.remove("opacity-0", "-translate-y-4");
        }
        if (adding) {
          adding.classList.add("opacity-0", "translate-y-4");
        }
        button.disabled = false;
        button.setAttribute("aria-disabled", "false");
        this.closeAllPopovers();
      }
    }

    getSectionsToRender() {
      const drawer = document.getElementById("Cart-Drawer");
      const drawerSectionId = drawer?.dataset?.sectionId || "cart-drawer";
      return [
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
        {
          id: "Cart-Drawer",
          section: drawerSectionId,
          selector: "[data-cart-drawer-content]",
        },
      ];
    }

    renderSections(sections = {}) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;
        const container = document.getElementById(section.id);
        if (!container) return;
        if (!section.selector) {
          container.innerHTML = html;
          return;
        }
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const element = parsed.querySelector(section.selector);
        if (element) {
          if (section.id === "Cart-Drawer") {
            const contentTarget = container.querySelector(
              "[data-cart-drawer-content]",
            );
            if (contentTarget) {
              contentTarget.innerHTML = element.innerHTML;
              if (window.webvista?.initLazyImageAnimation) {
                window.webvista.initLazyImageAnimation(contentTarget);
              }
            }
          } else {
            container.innerHTML = element.innerHTML;
          }
        }
      });
    }
  }

  customElements.define("cinematic-slider", CinematicSlider);
})();
